import { GetStaticProps } from 'next';
import AuthorPage from '@/screens/AuthorsPage/AuthorPage';
import { fetchAuthorBySlug } from '@/entities/Blogs/service/fetchBlogBySlug';
import { checkImage } from '@/common/app/utils/checkImage';
//import URL from '@/common/app/utils/urlsUtils';
import { getRedirectsPath } from '@/common/service/api/Redirects/getRedirectsPath';
import { fetchBlogs } from '@/entities/Blogs/service';
import {
    getAdditionalVideos,
    getLinks,
    getPublications,
    getYoutubeId,
} from '@/screens/AuthorsPage/utils';
import { buildRootUrl } from '@/common/app/utils/urlsUtils';
import { createStructuredAuthorData } from '@/entities/Activity/app/Creators';

type PageStaticProps = {
    slug: string;
};

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: 'blocking',
    };
}

export const getStaticProps: GetStaticProps<Record<string, unknown>, PageStaticProps> = async ({
    params,
}) => {
    try {
        if (!params) {
            throw new Error(`AUTHOR DETAILS: BAD PARAMS: ${params}`);
        }
        const pageContent = await fetchAuthorBySlug(params.slug);

        if (!pageContent) {
            const redirectPath = await getRedirectsPath(`/blog/authors/${params.slug}`);

            if (redirectPath === '/404/') {
                return {
                    notFound: true,
                };
            }

            return {
                revalidate: Number(process.env.CACHE_ENV),
                redirect: {
                    destination: redirectPath.toLowerCase(),
                    statusCode: 301,
                },
            };
        }

        const [publications, mainImage, topBlogs, allBlogs] = await Promise.all([
            getPublications(pageContent.slug, pageContent.publications),
            checkImage(`/common/main_${pageContent.slug}/ts`),
            fetchBlogs({ author: pageContent.slug, limit: 6, withRating: 1 }),
            fetchBlogs({ author: pageContent.slug, limit: 6 }),
        ]);

        const links = getLinks(pageContent.links);
        const additional_videos = getAdditionalVideos(pageContent.additional_videos);
        const video = getYoutubeId(pageContent.video);
        const breadcrumbs = [
            {
                url: `/blog/`,
                title: 'Blogs',
            },
            {
                url: `/blog/authors/`,
                title: 'Authors',
            },
            {
                url: `/blog/authors/${pageContent.slug}`.toLowerCase(),
                title: pageContent.title,
                unlink: true,
            },
        ];

        const meta = {
            title: `About ${pageContent.title} | Your TripShock Travel Expert`,
            description: `Discover articles by ${pageContent.title}, covering all the best things to do while traveling. Explore expert insights, tips, and industry updates to stay informed and inspired.`,
            canonical: buildRootUrl(`/blog/authors/${pageContent.slug}/`),
        };

        const structuredData = createStructuredAuthorData(pageContent, mainImage, links);

        return {
            props: {
                meta,
                structuredData,
                pageContent: {
                    ...pageContent,
                    publications,
                    links,
                    additional_videos,
                    video,
                },
                mainImage,
                topBlogs,
                allBlogs,
                breadcrumbs,
            },
            revalidate: 3600,
        };
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export default AuthorPage;
